import { artworkListItemSchema } from "./artworkItem.mjs";
import { parseBigintList, splitCsvOrArray } from "./util/queryParams.mjs";
import { createOffsetListResponseSchema, offsetPaginationInputSchema } from "./util/offsetPagination.mjs";
import { orpcSuccessVoidResponseSchema } from "./util/success.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/tack.ts
const tackSyncInputSchema = z.object({
	tackId: z.coerce.bigint(),
	operation: z.enum([
		"create",
		"update",
		"delete"
	])
});
const tackSyncErrors = {
	UNAUTHORIZED: {
		status: 401,
		message: "Invalid pixai-tack token"
	},
	NOT_FOUND: {
		status: 404,
		message: "Tack not found"
	},
	BAD_REQUEST: {
		status: 400,
		message: "Bad request"
	}
};
const curatedArtworksInputSchema = offsetPaginationInputSchema.extend({
	slug: z.string(),
	pageSize: z.coerce.number().int().min(1).max(24).default(24)
});
const curatedArtworksOutputSchema = createOffsetListResponseSchema(artworkListItemSchema).extend({
	totalPages: z.number().int().nonnegative(),
	hasNextPage: z.boolean()
});
const curatedArtworksErrors = { NOT_FOUND: {
	status: 404,
	message: "Tack not found"
} };
const tackTermSchema = z.object({
	id: z.string(),
	category: z.string(),
	name: z.string()
});
const tackItemSchema = z.object({
	id: z.string(),
	parentId: z.string().nullable().meta({
		deprecated: true,
		description: "Deprecated. Always returns `null`. Will be removed in a follow-up PR."
	}),
	codeName: z.string(),
	category: z.string(),
	defaultName: z.string(),
	mediaId: z.string().nullable(),
	weight: z.number().int(),
	createdAt: z.date(),
	updatedAt: z.date(),
	tackTerms: z.array(tackTermSchema).meta({
		deprecated: true,
		description: "Deprecated. Always returns `[]`; use `displayName` instead. Will be removed in a follow-up PR."
	}),
	thumbnailUrl: z.string().url().nullable(),
	displayName: z.string()
});
const galleryInputSchema = z.object({
	first: z.coerce.number().int().positive().max(50).default(50).describe("Number of tacks to fetch (max 50)"),
	lang: z.string().min(1).optional().describe("Optional user language code (e.g. `en`, `ja`, `zh`). Controls the `displayName` resolution: user-language term → English term → `defaultName`. When omitted, `displayName` falls through to `defaultName`.")
});
const galleryOutputSchema = z.object({ data: z.array(tackItemSchema) });
const trendingTackItemSchema = z.object({
	tackId: z.string(),
	artworkCount: z.object({
		"1h": z.number().int(),
		"6h": z.number().int(),
		"24h": z.number().int(),
		"7d": z.number().int()
	}),
	score: z.number(),
	tack: tackItemSchema
});
const trendingSortSchema = z.enum([
	"trending",
	"weekly",
	"daily"
]).default("trending").describe("Sort mode: trending (weighted score), weekly (7d count), daily (24h count)");
const trendingInputSchema = offsetPaginationInputSchema.extend({
	page: z.coerce.number().int().min(1).default(1).describe("Page number (1-based)"),
	pageSize: z.coerce.number().int().min(5).max(100).default(100).describe("Number of items per page (5–100)"),
	sort: trendingSortSchema,
	lang: z.string().min(1).default("en").describe("User language code (e.g. `en`, `ja`, `zh`). Controls the `displayName` resolution: user-language term → English term → `defaultName`. Defaults to `en`.")
});
const trendingOutputSchema = createOffsetListResponseSchema(trendingTackItemSchema).extend({ hasNextPage: z.boolean() });
const batchTackItemSchema = z.object({
	id: z.string(),
	codeName: z.string(),
	category: z.string(),
	defaultName: z.string(),
	weight: z.number().int(),
	thumbnailUrl: z.string().url().nullable(),
	displayName: z.string()
});
const batchInputSchema = z.object({
	lang: z.string().min(1).optional().describe("Optional user language code (e.g. `en`, `ja`, `zh`). Controls the `displayName` resolution: user-language term → English term → `defaultName`. When omitted, `displayName` falls through to `defaultName`."),
	ids: z.union([z.string(), z.array(z.string())]).transform((value, ctx) => parseBigintList(splitCsvOrArray(value), ctx, {
		min: 1,
		max: 100
	})).describe("Tack IDs to fetch (1–100). Accepts comma-separated (`?ids=1,2,3`), bracket notation (`?ids[]=1&ids[]=2`), or repeated (`?ids=1&ids=2`).")
});
const batchOutputSchema = z.object({ data: z.array(batchTackItemSchema) });
const autocompleteInputSchema = z.object({
	q: z.string().max(100).describe("Search query (empty string returns the default tack list)"),
	limit: z.coerce.number().int().min(1).max(50).default(20).describe("Number of results to return (1–50, default 20)"),
	lang: z.string().min(1).optional().describe("Optional user language code (e.g. `en`, `ja`, `zh`). Controls the `displayName` resolution: user-language term → English term → `defaultName`. When omitted, `displayName` falls through to `defaultName`.")
});
const autocompleteOutputSchema = z.object({ data: z.array(tackItemSchema) });
const tackContract = oc.prefix("/tack").tag("Tack").router({
	curatedArtworks: oc.route({
		summary: "Get curated artwork feed for a tack",
		method: "GET",
		path: "/{slug}/curated-artworks"
	}).input(curatedArtworksInputSchema).output(curatedArtworksOutputSchema).errors(curatedArtworksErrors),
	gallery: oc.route({
		summary: "List trending IP/character tacks for gallery display",
		description: "Returns tacks in the `ip` and `character` categories that currently appear in the trending set, ordered by trending score descending (ties broken by tackId descending).",
		method: "GET",
		path: "/gallery"
	}).input(galleryInputSchema).output(galleryOutputSchema),
	trending: oc.route({
		summary: "List trending tacks",
		description: "Returns trending tacks with page-based pagination. Use the `sort` param to control ordering: `trending` (default) sorts by weighted score, `weekly` by 7-day artwork count, `daily` by 24-hour artwork count.",
		method: "GET",
		path: "/trending"
	}).input(trendingInputSchema).output(trendingOutputSchema),
	syncFromPixaiTack: oc.route({
		summary: "Sync tack data from pixai-tack service",
		description: "Receives tack updates from pixai-tack, invalidates cache and updates Elasticsearch",
		method: "POST",
		path: "/sync"
	}).input(tackSyncInputSchema).output(orpcSuccessVoidResponseSchema).errors(tackSyncErrors),
	batch: oc.route({
		summary: "Batch get tacks by IDs",
		description: "Returns tacks matching the given IDs, ordered to match the input `ids`. Each tack includes a small (avatar-sized) `thumbnailUrl` resolved server-side and a resolved `displayName` (user-language term → English term → `defaultName`; falls through to `defaultName` when `lang` is omitted). Unknown IDs are silently omitted. Capped at 100 IDs per request.",
		method: "GET",
		path: "/batch"
	}).input(batchInputSchema).output(batchOutputSchema),
	autocomplete: oc.route({
		summary: "Autocomplete tack search with CJK support",
		description: "Prefix + fuzzy search over TackTermUnion. Kana input is converted to romaji for CJK coverage.",
		method: "GET",
		path: "/autocomplete"
	}).input(autocompleteInputSchema).output(autocompleteOutputSchema)
});
//#endregion
export { batchTackItemSchema, tackContract, tackItemSchema, tackTermSchema, trendingSortSchema, trendingTackItemSchema };
